import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useRealtimeDrivers } from 'Core/hooks';
import moment from 'moment';

//Components
import {
    SidePanel,
    SidePanelBody,
    SidePanelFooter,
    Button,
    Select,
    TimeFrame,
    useTimeFrameComponent,
    Form
} from '@gpstrackit/gtc-react-controls';
import { Grid } from '@mui/material';

// Actions
import { catalogLoad } from 'Redux/actions'
import {
    openModalAdvancedFilters,
    workOrdersSearchWorkOrderStatuses,
    workOrdersSettingsSearchWorkOrderTypes,
    reset
} from './redux/slice';

//Helpers
import { getInitialRange } from "Core/data/Helpers";

// Styles
import './index.scss';
const AdvancedFilters = ({ tableRef, formData, setFormData, initialState, applyChanges, hasFilters, savePreferences }) => {

    const dispatch = useDispatch()
    const { messages } = useIntl()

    // Redux States
    const { open, loading, statuses, workOrderTypes } = useSelector((state) => state.workOrdersWorkOrderTabAdvancedFiltersRedux);
    const dateformat = useSelector(state => state.securityRedux.user.dateformat);
    const driversFromState = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    
    // Sort drivers alphabetically by name using lodash (case insensitive)
    const drivers = useMemo(() => {
        return _.orderBy(driversFromState || [], [driver => driver.name.toLowerCase()], ['asc']);
    }, [driversFromState]);

    let severityList = [
        { id: "Low", name: messages['workOrder_createEdit_low'] },
        { id: "Medium", name: messages['workOrder_createEdit_medium'] },
        { id: "High", name: messages['workOrder_createEdit_high'] }
    ]

    const timeFrameOptions = [
        { id: "custom", label: messages['date_custom'] },
        { id: "today", label: messages['date_today'] },
        { id: "yesterday", label: messages['date_yesterday'] },
        { id: "week", label: messages['date_this_week'] },
        { id: "lastWeek", label: messages['date_last_week'] },
        { id: "thisMonth", label: messages['date_this_month'] },
        { id: "nextMonth", label: messages['date_this_nextMonth'] },
        { id: "lastMonth", label: messages['date_last_month'] },
        { id: "quarter", label: messages['date_this_quarter'] },
        { id: "lastQuarter", label: messages['date_last_quarter'] },
        { id: "next7days", label: messages['date_next_7days'] },
        { id: "next15days", label: messages['date_next_15days'] },
        { id: "next30Days", label: messages['date_next_30days'] },
        { id: "next90Days", label: messages['date_next_90days'] },
    ];

    const timeFrame = useTimeFrameComponent(timeFrameOptions, "thisMonth")

    //Form refs
    const formRef = Form.useForm();

    useEffect(() => {
        if (open) {
            dispatch(workOrdersSearchWorkOrderStatuses({}));
            dispatch(workOrdersSettingsSearchWorkOrderTypes({}));
            dispatch(catalogLoad(["drivers"]));
        }
    }, [open])

    useEffect(() => {

        if (open) {

            const { startDate, endDate } = timeFrame;
            let date = getInitialRange(timeFrame?.option?.id, startDate, endDate);
            let start = moment(date?.start).utc();
            let end = moment(date?.end).utc();

            let currentDateRange = {
                start: start.toISOString(),
                end: end.toISOString()
            }

            setFormData({ ...formData, rangeDate: currentDateRange, timeFrame: timeFrame?.option?.id })
            savePreferences([
                {
                    key: "rangeDate",
                    value: currentDateRange
                },
                {
                    key: "timeFrame",
                    value: timeFrame?.option?.id
                }
            ])
        }
    }, [timeFrame?.startDate, timeFrame?.endDate]);

    useEffect(() => {
        return () => {
            dispatch(reset())
        }
    }, [])

    useEffect(() => {
        if (open && formData) {
            if (formData?.timeFrame && formData?.rangeDate?.start && formData?.rangeDate?.end) {
                let currentTime = timeFrameOptions.find((item) => item?.id === formData.timeFrame)
                timeFrame.onChangeSelect(currentTime, formData.rangeDate.start, formData.rangeDate.end)
            }
            formRef?.setValues(formData);
        }
    }, [open])

    const clearFilters = () => {
        tableRef.addConditions(1, [{
            range: {
                fields: ["scheduledDate"],
                GTE: initialState?.rangeDate?.start,
                LTE: initialState?.rangeDate?.end
            }
        }]);
        const defaultPreferences = ["severity", "type", "status", "rangeDate", "timeFrame", 'driver']
            .map(key => ({ key, value: null }));
        savePreferences(defaultPreferences)
        setFormData(initialState);
        formRef?.resetValues();
        timeFrame.reset();
    }

    return (
        <SidePanel
            isOpen={open}
            onToggle={() => dispatch(openModalAdvancedFilters({ open: !open }))}
            style={{ zIndex: 2, width: '320px', transform: 'translateX(320px)' }}
            contentStyle={{ overflowY: "hidden" }}
            classSidePanel={hasFilters() ? "work-order-advanced-filters-available" : ""}
        >
            <SidePanelBody>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <h2 style={{ margin: 0 }}>
                            {messages['workOrder_tab_advancedFilters_title']}
                        </h2>
                        <span
                            className='btn-link'
                            onClick={clearFilters}
                        >
                            {messages['workOrder_tab_advancedFilters_clearAll']}
                        </span>
                    </Grid>
                </Grid>
                <div style={{ width: 270, marginTop: 10 }}>
                    <Form
                        ref={formRef.ref}
                        loading={loading}
                        onValidSubmit={() => applyChanges()}
                        onChangeValues={() => { }}
                        className="work-order-advanced-filters"
                    >
                        <Form.Item
                            name="status"
                            label={messages['workOrder_tab_advancedFilters_status']}
                        >
                            <Select
                                id="status"
                                type="text"
                                placeholder={messages['workOrder_tab_advancedFilters_status']}
                                textField="name"
                                valueField="id"
                                isClearable
                                isSearchable
                                options={statuses || []}
                                onChange={(value) => {
                                    setFormData({ ...formData, status: value })
                                    savePreferences([{ key: "status", value: value }])
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="scheduledAt"
                            label={messages['workOrder_tab_advancedFilters_scheduledDate']}
                        >
                            <div style={{ width: 270, marginTop: -10 }} className="work-order-advanced-filters-timeframe">
                                <TimeFrame
                                    isRequired
                                    filterSwitch={false}
                                    {...timeFrame}
                                    maxDate={moment().add(2, 'months').toDate()}
                                    dateFormat={dateformat || 'Y/m/d'}
                                    withTime={false}
                                    openDateRangePickerModalAutomatically={false}
                                    startDate={moment(formData?.rangeDate?.start)}
                                    endDate={moment(formData?.rangeDate?.end)}
                                />
                            </div>
                        </Form.Item>
                        <Form.Item
                            name="driver"
                            label={messages['workOrder_tab_advancedFilters_driver']}
                        >
                            <Select
                                id="driver"
                                type="text"
                                placeholder={messages['workOrder_tab_advancedFilters_driver']}
                                textField="name"
                                valueField="driverId"
                                isClearable
                                isSearchable
                                options={drivers || []}
                                onChange={(value) => {
                                    setFormData({ ...formData, driver: value })
                                    savePreferences([{ key: "driver", value: value }])
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="type"
                            label={messages['workOrder_tab_advancedFilters_type']}
                        >
                            <Select
                                id="type"
                                type="text"
                                placeholder={messages['workOrder_tab_advancedFilters_type']}
                                textField="name"
                                valueField="id"
                                isClearable
                                isSearchable
                                options={workOrderTypes || []}
                                onChange={(value) => {
                                    setFormData({ ...formData, type: value })
                                    savePreferences([{ key: "type", value: value }])
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="severity"
                            label={messages['workOrder_tab_advancedFilters_severity']}
                        >
                            <Select
                                id="severity"
                                type="text"
                                placeholder={messages['workOrder_tab_advancedFilters_severity']}
                                textField="name"
                                valueField="id"
                                isClearable
                                isSearchable
                                options={severityList || []}
                                onChange={(value) => {
                                    setFormData({ ...formData, severity: value })
                                    savePreferences([{ key: "severity", value: value }])
                                }}
                            />
                        </Form.Item>
                    </Form>
                </div>
            </SidePanelBody>
            <SidePanelFooter>
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={() => dispatch(reset({ open: false }))}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading || (!timeFrame?.startDate || !timeFrame?.endDate)}
                        onClick={() => formRef?.submit()}
                        variant="primary"
                    >
                        {messages["apply"]}
                    </Button>
                </div>
            </SidePanelFooter>
        </SidePanel>
    )
}

export default AdvancedFilters